import { toast } from "react-toastify";
import { defaultToastOptions } from "../../utils/misc";
import { CheckinTypeOption, MonthOption, TypeSelection } from "./types";
import {
  BadgeTemplate,
  client as ppSdk,
} from "@pushpress/committed-club-typescript-sdk";
import { getAccessToken } from "../../utils/tokens";
import { domain, getHttpProtocol } from "../../utils/requests";
import {
  ABLY_API_KEY_PROD,
  ABLY_API_KEY_STAGE,
  ABLY_API_KEY_DEV,
} from "../MigrationAssistant/constants";
import { isProd, isStage } from "../../env";

export const committedClubChannelName = "global.committedclub";
export const getCommittedClubCheckinChannelName = (clientUuid: string) =>
  `client.committedclub.${clientUuid}`;

export const configureSDK = () => {
  const token = getAccessToken();
  ppSdk.setConfig({
    baseUrl: `${getHttpProtocol}//committedclub.${domain}.com`,
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

export const getAblyApiKey = () => {
  if (isProd()) return ABLY_API_KEY_PROD;
  if (isStage()) return ABLY_API_KEY_STAGE;
  return ABLY_API_KEY_DEV;
};

export const showSuccessfulToast = (text: string) => {
  toast.success(text, defaultToastOptions);
};

export const showErrorToast = (text: string) => {
  toast.error(text, defaultToastOptions);
};

export const copyToClipboard = async (text: string, toastMessage: string) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (err) {
    // Fallback to old method
    const textarea = document.createElement("textarea");

    textarea.value = text;
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand("copy"); // Deprecated, but still works in older browsers
    document.body.removeChild(textarea);
  } finally {
    showSuccessfulToast(toastMessage);
  }
};

export const exportMonthProgressToCSV = (
  csvContent: string,
  filename: string,
) => {
  // Create a Blob object and trigger the download
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `${filename}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const getOptionFromMonthIndex = (monthIdx: number): MonthOption => {
  const nextMonth = new Date(new Date().getFullYear(), monthIdx);
  const monthName = nextMonth.toLocaleString("default", { month: "long" });
  return { label: monthName, value: monthIdx };
};

export const getMonthOptionFromDateString = (
  dateString: string,
): MonthOption => {
  const monthNumber = parseInt(dateString.split("-")[1], 10);
  const monthOption = getOptionFromMonthIndex(monthNumber - 1); // zero-indexed
  return monthOption;
};

// calculate start date
export const getStartDateFromMonthOption = (option: MonthOption): string => {
  const today = new Date();
  const currentMonth = today.getMonth();
  const currentYear = today.getFullYear();
  const clubStartMonth = option.value;
  const clubStartYear =
    clubStartMonth < currentMonth ? currentYear + 1 : currentYear;

  // Create a date and format it in the local timezone
  const startDate = new Date(clubStartYear, clubStartMonth, 1);
  // Format as "YYYY-MM-DD" using Canadian locale which guarantees this format
  // This is more reliable than manually constructing the date string
  return startDate.toLocaleDateString("en-CA");
};

export const getCurrentMonthOption = (): MonthOption => {
  const today = new Date();
  const monthIndex = today.getMonth();
  return getOptionFromMonthIndex(monthIndex);
};

export const getNextMonths = (futureMonths = 3): MonthOption[] => {
  const months = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const monthsToShow = 1 + futureMonths;
  const currentMonth = new Date().getMonth();
  const result = [];

  for (let i = 0; i < monthsToShow; i++) {
    const monthIndex = (currentMonth + i) % 12;
    result.push({
      label: months[monthIndex],
      value: monthIndex,
    });
  }

  return result;
};

export const areArraysEqual = (arr1, arr2): boolean => {
  if (arr1.length !== arr2.length) return false;
  return [...arr1].sort().join() === [...arr2].sort().join();
};

export const getSetupItemTitleFromTemplate = (
  template?: BadgeTemplate,
): string[] => {
  if (!template) {
    return [];
  }

  const { criteria, criteriaTotal } = template;

  const eventTypes =
    criteria.includeEvents && criteria.includeOpenGym
      ? "BOTH"
      : criteria.includeEvents
      ? "EVENTS"
      : criteria.includeOpenGym
      ? "OPENGYMS"
      : "NONE";

  const classClause = classCheckinOptions.find(
    (o) => o.value === criteria.classSelectionStrategy,
  ).overview;
  const appointmentClause = appointmentCheckinOptions.find(
    (o) => o.value === criteria.appointmentSelectionStrategy,
  ).overview;
  const eventClause = eventCheckinOptions.find(
    (o) => o.value === eventTypes,
  ).overview;

  return [
    `${criteriaTotal} ${criteriaTotal == 1 ? "check-in" : "check-ins"} / Month`,
    `Counting ${classClause}, ${appointmentClause}, ${eventClause}`,
  ];
};

export const classCheckinOptions: TypeSelection[] = [
  { value: "ALL", label: "All class check-ins", overview: "all classes" },
  {
    value: "SPECIFIC",
    label: "Class check-ins of certain types",
    overview: "some classes",
  },
  { value: "NONE", label: "No class check-ins", overview: "no classes" },
];

export const appointmentCheckinOptions: TypeSelection[] = [
  {
    value: "ALL",
    label: "All appointment check-ins",
    overview: "all appointments",
  },
  {
    value: "SPECIFIC",
    label: "Appointment check-ins of certain types",
    overview: "some appointments",
  },
  {
    value: "NONE",
    label: "No appointment check-ins",
    overview: "no appointments",
  },
];

export const eventCheckinOptions: CheckinTypeOption[] = [
  {
    value: "BOTH",
    label: "All event and open gym check-ins",
    overview: "all open gyms and events",
  },
  { value: "EVENTS", label: "Only event check-ins", overview: "all events" },
  {
    value: "OPENGYMS",
    label: "Only open gym check-ins",
    overview: "all open gyms",
  },
  {
    value: "NONE",
    label: "No event or open gym check-ins",
    overview: "no open gym or events",
  },
];
